import { useEffect, useState } from "react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore } from "@/stores/filterStore";
import { fmt, toolLabel, prettyModelId, superGroupOf, TOOL_LINE_COLORS } from "@/lib/format";
import { GroupIcon } from "@/lib/groupIcons";
import MiniSelect from "@/components/MiniSelect";
import { cachedCall, invalidateCache } from "@/lib/queryCache";
import ToolEngineBreakdown from "./ToolEngineBreakdown";
import ModelBreakdown from "./ModelBreakdown";
import UsageTimeline from "./UsageTimeline";
import UserChargeHistory from "./UserChargeHistory";
import CanonicalUserView from "@/components/CanonicalUserView";
import StgQaCreditButton from "./StgQaCreditButton";
import { buildListAdjustPayload } from "@/lib/stgQaGrant";

const RANGES = [
  { value: "7d", label: "7일" },
  { value: "30d", label: "30일" },
  { value: "all", label: "전체" },
];

// 워딩 정리(2026-07-15): "사용 메타데이터"는 낯선 기술 용어라, 결제/구독 화면에서 흔히
// 쓰는 "이용 내역"으로. 탭도 도구별/모델별을 따로 나눠 한 탭에 표 두 개가 겹치지 않게 함.
const TABS = [
  { id: "tool", label: "도구별" },
  { id: "model", label: "모델별" },
  { id: "timeline", label: "이용 내역" },
  { id: "charges", label: "충전 내역" },
];

// "전체 사용자" 표에서 행을 클릭하면 우측 Drawer(TopUsersTable.jsx)에 이 컴포넌트가 열린다.
// 예전엔 /users/:userId 별도 페이지로 이동해야 봤는데(페이지 전환이 비효율적이라는 피드백),
// 그 다음엔 표 안에 인라인으로 펼쳤다가 표가 옆으로 너무 넓어져서(2026-07-15) 다시 사이드
// 패널로 옮김 — 콘텐츠(도구·엔진별/모델별 사용량 + 세션별 사용 이력)는 동일. 실제 대화
// 내용(기사 원문)은 안 보여주고 시각/도구/모델/토큰/비용 같은 사용 메타데이터만 — 크레딧
// 정산/이상탐지 목적이라 그 정도로 충분하고 원문까지 보이면 오히려 과한 개인정보 노출.
//
// 도구/모델 필터(2026-07-18) — 서버사이드 필터(?tool=/&model=)라 KPI·표·이용 내역
// 페이지네이션까지 전부 같은 기준으로 걸러진다. 필터 옵션 목록은 "필터 없는 조회" 결과로
// 만들어 유지한다(필터를 걸면 응답의 도구/모델 목록도 줄어들어 옵션이 사라져버리므로).
export default function UserInlineDetail({ userId, onAdjusted }) {
  // B-1 (2026-09-28): 상세 조회도 현재 view env(prod/stg)를 따라가야 목록과 같은 환경을
  // 본다. 종전엔 env 를 안 실어 백엔드 기본값(prod)으로 조회 → STG 뷰에서도 상세가 prod 를
  // 봐 "데이터 없음"이 났다. useAnalyticsQuery 를 안 거치는 직접 호출이라 여기서 명시 주입한다.
  const env = useFilterStore((s) => s.env);
  const [range, setRange] = useState("7d");
  const [activeTab, setActiveTab] = useState(TABS[0].id); // 페이지가 너무 길어져서(2026-07-15) 탭으로 분리
  const [toolFilter, setToolFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [toolOptions, setToolOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [sessionPage, setSessionPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Canonical Identity 진입 토글 — data.canonicalUserId 가 있을 때만 의미가 있다.
  const [showCanonical, setShowCanonical] = useState(false);
  // STG QA 크레딧 지급 성공 시 상세를 강제 재조회하기 위한 키(캐시 무효화와 함께 사용).
  const [reloadKey, setReloadKey] = useState(0);

  // 기간/필터를 바꾸면 이전 페이지 번호가 새 결과 범위 밖일 수 있어 1페이지로 되돌림.
  useEffect(() => {
    setPage(1);
    setSessionPage(1);
  }, [range, toolFilter, modelFilter]);

  // 다른 사용자를 열면 canonical 패널은 닫는다(이전 사용자 상태 누수 방지).
  useEffect(() => {
    setShowCanonical(false);
  }, [userId]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    // 3분 캐시 — 같은 사용자를 다시 열거나 필터를 되돌리면 즉시(2026-07-18 속도 피드백).
    // 캐시 키에 env 를 포함(B-1) — prod↔stg 전환 시 반대 환경의 캐시가 재사용되면 안 된다.
    cachedCall(
      `user|${userId}|${env}|${range}|${page}|${sessionPage}|${toolFilter}|${modelFilter}|${reloadKey}`,
      () => analyticsApi.userDetail(userId, {
        range, page, pageSize: 50, sessionPage, sessionPageSize: 20,
        tool: toolFilter || undefined, model: modelFilter || undefined,
        env,
      }, ctrl.signal)
    )
      .then((res) => {
        setData(res);
        // 필터 없는 응답에서만 옵션 목록 갱신 — 필터 중에도 전체 선택지가 유지되게.
        // 목록은 표시 라벨 기준 가나다순(2026-07-18 피드백 — 응답 순서 그대로면 뒤죽박죽).
        if (!toolFilter && !modelFilter) {
          setToolOptions(
            [...new Set((res.byToolEngine || []).map((r) => r.tool).filter(Boolean))]
              .sort((a, b) => toolLabel(a).localeCompare(toolLabel(b), "ko"))
          );
          setModelOptions(
            [...new Set((res.byModel || []).map((r) => r.modelId).filter(Boolean))]
              .sort((a, b) => prettyModelId(a).localeCompare(prettyModelId(b), "ko"))
          );
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setError(err instanceof ApiError ? err : new ApiError(String(err), 0, "UNKNOWN"));
        }
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [userId, env, range, page, sessionPage, toolFilter, modelFilter, reloadKey]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-ink-400">기간</span>
        {RANGES.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setRange(r.value)}
            className={`rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors ${
              range === r.value ? "border-brand-600 bg-brand-600 text-white" : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
            }`}
          >
            {r.label}
          </button>
        ))}
        <span className="ml-1" />
        <MiniSelect
          rounded="rounded-full"
          value={toolFilter}
          onChange={setToolFilter}
          options={[
            { value: "", label: "도구: 전체" },
            ...toolOptions.map((t) => ({
              value: t,
              label: toolLabel(t),
              icon: <GroupIcon superGroup={superGroupOf(t)} color={TOOL_LINE_COLORS[superGroupOf(t)]} className="h-3.5 w-3.5" />,
            })),
          ]}
        />
        <MiniSelect
          rounded="rounded-full"
          value={modelFilter}
          onChange={setModelFilter}
          options={[{ value: "", label: "모델: 전체" }, ...modelOptions.map((m) => ({ value: m, label: prettyModelId(m) }))]}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          불러오지 못했습니다 — {error.message}
        </div>
      )}
      {loading && !data && <div className="text-sm text-ink-400">불러오는 중…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <MiniKpi label="크레딧 사용" value={fmt.nc(data.kpi?.ncUsed || 0)} />
            <MiniKpi label="턴 수" value={fmt.int(data.kpi?.requestCount || 0)} />
            <MiniKpi label="입력 토큰" value={fmt.int(data.kpi?.inputTokens || 0)} />
            <MiniKpi label="출력 토큰" value={fmt.int(data.kpi?.outputTokens || 0)} />
          </div>

          {/* STG 뷰 전용 — QA 테스트 크레딧 지급(B-2). env==="stg" 에서만 노출.
              PROD 뷰에선 렌더되지 않아 기존 PROD 지급 UI 와 완전히 분리된다. */}
          {env === "stg" && (
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
              <span className="text-xs text-amber-800">
                🟠 STAGING · 테스트 크레딧 (PROD와 무관)
                {data.user?.balance != null && (
                  <b className="ml-2 tabular-nums">현재 {Number(data.user.balance).toLocaleString()} NC</b>
                )}
              </span>
              <StgQaCreditButton
                userId={userId}
                email={data.user?.email}
                currentBalance={data.user?.balance}
                onGranted={({ amount } = {}) => {
                  // 상세 갱신(기존) — 상세 캐시 무효화 + 재조회로 balance 즉시 반영.
                  invalidateCache("user|");
                  setReloadKey((k) => k + 1);
                  // 목록 갱신 — 기존 PROD adminAdjust 와 같은 콜백 계약({userIds, amount})으로
                  // 상위(CreditsSection.handleGrantSuccess)에 알려 refreshTick+fresh=1+낙관적
                  // override 로 사용자 목록 balance 를 갱신한다. 전체 페이지 refresh 는 하지 않는다.
                  const payload = buildListAdjustPayload(userId, amount);
                  if (typeof onAdjusted === "function" && payload) onAdjusted(payload);
                }}
              />
            </div>
          )}

          {/* Canonical Identity 진입 — canonicalUserId 가 있을 때만 노출. 없으면 기존 UI 와
              완전히 동일(아무것도 안 그림). 클릭 시 같은 drawer 안에서 CanonicalUserView 를
              펼친다(새 App route 없음). 404/실패면 CanonicalUserView 가 onFallback 으로
              스스로 닫혀 기존 화면이 유지된다. */}
          {data.canonicalUserId && (
            <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
              <button
                type="button"
                onClick={() => setShowCanonical((v) => !v)}
                className="text-sm font-medium text-brand-700 hover:underline"
              >
                동일 사용자로 연결된 로그인 {data.canonicalLinkedCount || 0}개
                {showCanonical ? " — 접기" : " — 보기"}
              </button>
              {showCanonical && (
                <div className="mt-2 border-t border-brand-200 pt-2">
                  <CanonicalUserView
                    canonicalUserId={data.canonicalUserId}
                    onFallback={() => setShowCanonical(false)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeTab === t.id ? "bg-brand-600 text-white shadow-sm" : "text-ink-500 hover:bg-ink-100 hover:text-ink-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "tool" && <ToolEngineBreakdown byToolEngine={data.byToolEngine} />}
          {activeTab === "model" && <ModelBreakdown byModel={data.byModel} />}
          {activeTab === "charges" && <UserChargeHistory charges={data.charges} />}
          {activeTab === "timeline" && (
            <UsageTimeline
              timeline={data.timeline?.items}
              page={data.timeline?.page || 1}
              pageSize={data.timeline?.pageSize || 50}
              total={data.timeline?.total || 0}
              onPageChange={setPage}
              sessions={data.sessions?.items}
              sessionPage={data.sessions?.page || 1}
              sessionPageSize={data.sessions?.pageSize || 20}
              sessionTotal={data.sessions?.total || 0}
              onSessionPageChange={setSessionPage}
            />
          )}
        </>
      )}
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="rounded-lg border border-ink-200 bg-white p-2.5">
      <div className="text-[11px] text-ink-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-ink-900">{value}</div>
    </div>
  );
}
